import React from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/shared/config';
import { BlogPost } from '@/features/blog/api';
import { useSafeThemeClasses } from '@/shared/theme';

interface BlogTileProps {
	post: BlogPost;
	onDelete?: (id: string) => void;
}

const BlogTile: React.FC<BlogTileProps> = ({ post }) => {
	const theme = useSafeThemeClasses();
	const {
		id,
		slug,
		title,
		excerpt,
		published_at,
		tags,
		reading_time_minutes
	} = post;

	// Format date to be more readable
	const formattedDate = published_at 
		? new Date(published_at).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
		: 'Draft';

	// const handleDelete = (e: React.MouseEvent) => {
	// 	e.preventDefault();
	// 	e.stopPropagation();

	// 	if (onDelete && id) {
	// 		if (window.confirm('Are you sure you want to delete this blog post?')) {
	// 			onDelete(id);
	// 		}
	// 	}
	// };

	return (
		<Link
			to={`${ROUTES.BLOG_POST(slug || id)}`}
			className={`group block h-full overflow-hidden rounded-3xl ${theme.card} shadow-sm hover:shadow-md transition-all duration-300`}
		>
			{/* Featured Image */}
			{post.featured_image_url && (
				<div className="w-full h-48 overflow-hidden">
					<img 
						src={post.featured_image_url} 
						alt={title}
						className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
					/>
				</div>
			)}
			
			<div className="p-6">
				{/* Date and Reading Time */}
				<div className={`flex items-center text-xs ${theme.textSecondary} mb-2`}>
					<span>{formattedDate}</span>
					{reading_time_minutes && (
						<>
							<span className="mx-2">•</span>
							<span>{reading_time_minutes} min read</span>
						</>
					)}
				</div>

				{/* Title */}
				<h3 className={`text-xl font-semibold mb-3 ${theme.textHeading} group-hover:text-primary transition-colors duration-300`}>
					{title}
				</h3>

				{/* Excerpt */}
				<p className={`text-sm ${theme.textSecondary} group-hover:text-primary transition-colors mb-4 line-clamp-3`}>
					{excerpt}
				</p>

				{/* Tags */}
				{tags && tags.length > 0 && (
					<div className="flex flex-wrap gap-1 mb-4">
						{tags.slice(0, 3).map((tag, index) => (
							<span 
								key={index}
								className={`text-xs px-2 py-1 ${theme.background} ${theme.textSecondary} rounded-md`}
							>
								{tag}
							</span>
						))}
						{tags.length > 3 && (
							<span className={`text-xs px-2 py-1 ${theme.textSecondary}`}>
								+{tags.length - 3} more
							</span>
						)}
					</div>
				)}

				{/* Admin Actions
				{onDelete && (
					<div className="flex justify-between mt-4 pt-4 border-t border-base-content/10">
						<Link
							to={`/blog/edit/${id}`}
							onClick={(e) => e.stopPropagation()}
							className={`inline-block py-2 px-4 text-center rounded-lg transition-all duration-300 text-sm font-medium border border-primary/30 hover:bg-primary/10 hover:border-primary/50 ${theme.textPrimary} hover:scale-105`}
						>
							Edit
						</Link>
						<div
							onClick={handleDelete}
							className={`inline-block py-2 px-4 text-center rounded-lg transition-all duration-300 text-sm font-medium border border-error/30 hover:bg-error/10 hover:border-error/50 ${theme.textError} cursor-pointer hover:scale-105`}
							role="button"
							tabIndex={0}
						>
							Delete
						</div>
					</div>
				)} */}
			</div>
		</Link>
	);
};

export default BlogTile;
